/**
 * NATIVA — Product Gallery Override
 *
 * Size change  → gallery completely unchanged
 * Color change → highlight thumbnail + scroll main image to that color
 *
 * Color vs Size detection:
 *   _nativaLastMediaId tracks the selected color variant's featured_media.
 *   Initialised from [data-selected-variant] on load; updated only inside
 *   updateMedia when a genuine color change is detected.
 *   Thumbnail clicks do NOT update it — so browsing the gallery never
 *   causes a subsequent size change to be misread as a color change.
 */
(function () {
  'use strict';

  var EDGE_PX = 24;

  // ── Helper: scroll thumbnail strip with edge-trigger logic ────────────────
  function scrollThumbnailStrip(slider, thumbnail) {
    if (!slider || !thumbnail) return;

    var sliderRect = slider.getBoundingClientRect();
    var thumbRect  = thumbnail.getBoundingClientRect();

    var thumbL = thumbRect.left  - sliderRect.left + slider.scrollLeft;
    var thumbR = thumbRect.right - sliderRect.left + slider.scrollLeft;
    var visL   = slider.scrollLeft;
    var visR   = slider.scrollLeft + slider.clientWidth;

    if (thumbL < visL) {
      slider.scrollTo({ left: thumbL, behavior: 'smooth' });
    } else if (thumbR > visR) {
      slider.scrollTo({ left: thumbR - slider.clientWidth, behavior: 'smooth' });
    } else if (thumbR > visR - EDGE_PX) {
      slider.scrollTo({ left: thumbL, behavior: 'smooth' });
    } else if (thumbL < visL + EDGE_PX) {
      slider.scrollTo({ left: thumbR - slider.clientWidth, behavior: 'smooth' });
    }
  }

  // ── Patch 1: Bidirectional thumbnail strip scrolling ──────────────────────
  function patchMediaGallery() {
    var MediaGalleryClass = customElements.get('media-gallery');
    if (!MediaGalleryClass) return false;

    MediaGalleryClass.prototype.setActiveThumbnail = function (thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach(function (el) {
        el.removeAttribute('aria-current');
      });
      var btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', 'true');

      scrollThumbnailStrip(this.elements.thumbnails.slider, thumbnail);
    };

    return true;
  }

  // ── Patch 2: Variant change — distinguish color vs size ───────────────────
  function patchProductInfo() {
    var ProductInfoClass = customElements.get('product-info');
    if (!ProductInfoClass) return false;

    ProductInfoClass.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      var mediaGallery = this.querySelector('media-gallery');
      if (!mediaGallery) return;

      var mediaId = this.dataset.section + '-' + variantFeaturedMediaId;

      // Compare against last known color media ID
      var isColorChange = (this._nativaLastMediaId !== mediaId);

      // Always update the stored ID so subsequent size changes stay stable
      this._nativaLastMediaId = mediaId;

      if (!isColorChange) {
        // Size-only change → do nothing to the gallery
        var mc  = this.productModal && this.productModal.querySelector('.product-media-modal__content');
        var nmc = html && html.querySelector('product-modal .product-media-modal__content');
        if (mc && nmc) mc.innerHTML = nmc.innerHTML;
        return;
      }

      // ── Color changed: update main image + thumbnail ───────────────────────

      var viewer = mediaGallery.elements && mediaGallery.elements.viewer;
      if (viewer) {
        var targetSlide = viewer.querySelector('[data-media-id="' + mediaId + '"]');
        if (targetSlide) {
          viewer.querySelectorAll('[data-media-id]').forEach(function (el) {
            el.classList.remove('is-active');
          });
          targetSlide.classList.add('is-active');
          targetSlide.parentElement.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
        }
      }

      var tc = mediaGallery.elements && mediaGallery.elements.thumbnails;
      if (tc) {
        var activeThumbnail = tc.querySelector('[data-target="' + mediaId + '"]');
        if (activeThumbnail) mediaGallery.setActiveThumbnail(activeThumbnail);
      }

      var modalContent    = this.productModal && this.productModal.querySelector('.product-media-modal__content');
      var newModalContent = html && html.querySelector('product-modal .product-media-modal__content');
      if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
    };

    return true;
  }

  // ── Initialise each product-info ──────────────────────────────────────────
  function initTracking() {
    document.querySelectorAll('product-info').forEach(function (productInfoEl) {
      // Start as null: gallery shows image 1 by default, so the first color
      // selection (any color) should always scroll to that color's image.
      productInfoEl._nativaLastMediaId = null;
    });
  }

  // ── Reset gallery to first image on load (don't auto-jump to variant image) ─
  function resetGalleryToFirst() {
    document.querySelectorAll('product-info').forEach(function(productInfoEl) {
      var mediaGallery = productInfoEl.querySelector('media-gallery');
      if (!mediaGallery || !mediaGallery.elements) return;

      var viewer = mediaGallery.elements.viewer;
      if (viewer) {
        var slides = Array.from(viewer.querySelectorAll('li[data-media-id]'));
        slides.forEach(function(sl, i) { sl.classList.toggle('is-active', i === 0); });
        viewer.scrollLeft = 0;
      }

      var tc = mediaGallery.elements.thumbnails;
      if (tc) {
        tc.querySelectorAll('li[data-target] button').forEach(function(btn, i) {
          btn.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        });
        if (tc.slider) tc.slider.scrollLeft = 0;
      }
    });
  }

  // ── Run everything ────────────────────────────────────────────────────────
  function runPatches() {
    patchMediaGallery();
    patchProductInfo();
    initTracking();
    resetGalleryToFirst();
  }

  if (customElements.get('media-gallery') && customElements.get('product-info')) {
    runPatches();
  } else {
    Promise.all([
      customElements.whenDefined('media-gallery'),
      customElements.whenDefined('product-info'),
    ]).then(runPatches);
  }
})();
